import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Card, CardScroll, Div, Group, Header, Panel, Skeleton, Text, Title } from '@vkontakte/vkui';
import { Icon28BookOutline, Icon28SchoolOutline } from '@vkontakte/icons';
import { AppShell, ScreenHeader } from '../../components/Layout/AppShell';
import { EmptyState } from '../../components/States/States';
import { DAILY_PANEL } from '../../routing/routing';
import { categoryImagePath } from '../../utils/assets';
import { fitTextSize, pluralize } from '../../utils/text';

function DailyWordSkeleton() {
  return (
    <Div className="space-y-3">
      <Skeleton height={300} />
      <Skeleton height={52} />
    </Div>
  );
}

export function DailyPanel({ nav = DAILY_PANEL, dailyWords, loading, onBack, openWord, startTraining }) {
  const trainingLocked = dailyWords.length < 3;
  const [activeIndex, setActiveIndex] = useState(0);
  const cardScrollRef = useRef(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [dailyWords.length]);

  useEffect(() => {
    const root = cardScrollRef.current;
    if (!root || dailyWords.length === 0) {
      return undefined;
    }

    const scrollContainer = root.querySelector('[class*="HorizontalScroll__in"]');
    const cards = Array.from(root.querySelectorAll('[data-daily-card="true"]'));
    if (!scrollContainer || cards.length === 0) {
      return undefined;
    }

    function updateActiveCard() {
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      let nextIndex = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          nextIndex = index;
        }
      });

      setActiveIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    }

    updateActiveCard();
    scrollContainer.addEventListener('scroll', updateActiveCard, { passive: true });
    window.addEventListener('resize', updateActiveCard);

    return () => {
      scrollContainer.removeEventListener('scroll', updateActiveCard);
      window.removeEventListener('resize', updateActiveCard);
    };
  }, [dailyWords]);

  return (
    <Panel nav={nav}>
      <ScreenHeader title="Слова на сегодня" onBack={onBack} />
      <AppShell>
        <Group header={<Header indicator={dailyWords.length ? `${activeIndex + 1} из ${dailyWords.length}` : undefined}>Подборка</Header>}>
          {loading && dailyWords.length === 0 ? (
            <DailyWordSkeleton />
          ) : dailyWords.length > 0 ? (
            <>
              <Div>
                <Text className="text-[var(--vkui--color_text_secondary)]">
                  {dailyWords.length} {pluralize(dailyWords.length, ['слово', 'слова', 'слов'])} на сегодня
                </Text>
              </Div>
              <CardScroll size={false} showArrows getRootRef={cardScrollRef}>
                {dailyWords.map((item) => (
                  <Card
                    key={item.id}
                    data-daily-card="true"
                    mode="outline"
                    className="my-1 mr-3 flex h-[300px] w-[272px] shrink-0 flex-col sm:w-[360px]"
                  >
                    <Div className="flex h-full flex-col text-center">
                      {item.category ? (
                        <div className="mb-3 flex shrink-0 items-center justify-center gap-2">
                          {item.category.image ? <Avatar size={28} src={categoryImagePath(item.category.image)} /> : <Avatar size={28}><Icon28SchoolOutline /></Avatar>}
                          <Text>{item.category.name}</Text>
                        </div>
                      ) : null}
                      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden">
                        <Title level="1" className="break-words" style={{ fontSize: `${fitTextSize(item.word, 10, 35, 20)}px` }}>{item.word}</Title>
                        <Text className="text-[var(--vkui--color_text_secondary)]" style={{ fontSize: `${fitTextSize(`[${item.transcription}]`, 23, 14, 11)}px` }}>[{item.transcription}]</Text>
                        <Text className="mt-2 break-words" style={{ fontSize: `${fitTextSize(item.translation, 20, 16, 12)}px` }}>{item.translation}</Text>
                      </div>
                    </Div>
                  </Card>
                ))}
              </CardScroll>
            </>
          ) : !loading ? (
            <EmptyState text="На сегодня слов пока нет. Загляните позже или откройте категории и словарь." />
          ) : null}
        </Group>

        <Group>
          <Div>
            <Button size="l" stretched before={<Icon28BookOutline />} onClick={() => startTraining('daily', 0, DAILY_PANEL)} disabled={trainingLocked || loading}>
              Тренировать слова
            </Button>
            <Text className="mt-3 text-center text-[var(--vkui--color_text_secondary)]">
              {loading && dailyWords.length === 0
                ? 'Загружаем сегодняшнюю подборку.'
                : trainingLocked
                  ? 'Сегодня подборка короткая, поэтому тренировка пока недоступна.'
                  : 'Быстрая тренировка займёт 1–2 минуты и сразу покажет результат.'}
            </Text>
          </Div>
        </Group>
      </AppShell>
    </Panel>
  );
}
